import {
	type UseMutationOptions,
	useMutation,
	useQueryClient
} from '@tanstack/react-query'
import {
	type HttpUpdateExpertiseStatus,
	httpUpdateExpertiseStatus
} from '@/features/service-vetting/http/services-vetting.http.ts'
import type { Service } from '@/features/service-vetting/types.ts'
import { errorHandler } from '@/helpers/utils'
import { QUERY_KEYS } from '@/query-client'

export const useUpdateServiceExpertiseStatus = (
	{ serviceId, expertiseId }: { serviceId: number; expertiseId: number },
	options?: UseMutationOptions<
		unknown,
		unknown,
		HttpUpdateExpertiseStatus['payload']
	>
) => {
	const queryClient = useQueryClient()
	return useMutation({
		...options,
		mutationFn: (payload) =>
			httpUpdateExpertiseStatus({
				urlParams: { serviceId, expertiseId },
				payload,
				params: {
					response_type: 'small'
				}
			}),
		onError: (error, variables, onMutateResult, context) => {
			queryClient.setQueriesData(
				{ queryKey: [QUERY_KEYS.SERVICE_VETTING, serviceId] },
				context
			)
			errorHandler(error)
			options?.onError?.(error, variables, onMutateResult, context)
		},
		onMutate: async (variables) => {
			const queryKey = [QUERY_KEYS.SERVICE_VETTING, serviceId]

			// Cancel any outgoing refetches (so they don't overwrite our optimistic update)
			await queryClient.cancelQueries({ queryKey })

			// Snapshot previous values
			const previousData = queryClient.getQueryData<Service>(queryKey)

			// Update cached data
			if (previousData) {
				queryClient.setQueriesData<Service>(
					{ queryKey },
					{
						...previousData,
						expertise: {
							...previousData.expertise,
							status: variables.status
						}
					}
				)
			}
			return previousData
		}
	})
}
